import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { ordenesServicioAPI } from '../../lib/ordenesApi';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { toast } from 'sonner';
import {
    ClipboardList,
    MapPin,
    Phone,
    Clock,
    CheckCircle,
    Play,
    Navigation,
    User,
    Car,
    Calendar,
    RefreshCw,
    ChevronRight,
    Loader2,
} from 'lucide-react';

const ESTADO_CONFIG = {
    pendiente: { label: 'Pendiente', color: 'bg-yellow-100 text-yellow-700', icon: Clock },
    en_proceso: { label: 'En Proceso', color: 'bg-blue-100 text-blue-700', icon: Play },
    realizada: { label: 'Realizada', color: 'bg-green-100 text-green-700', icon: CheckCircle },
};

const PRIORIDAD_CONFIG = {
    alta: { color: 'bg-red-500', label: 'Alta' },
    media: { color: 'bg-yellow-500', label: 'Media' },
    baja: { color: 'bg-green-500', label: 'Baja' },
};

export default function TecnicoMobileView() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [ordenes, setOrdenes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [actionLoading, setActionLoading] = useState(null);

    useEffect(() => {
        loadOrdenes();
    }, []);

    const loadOrdenes = async (isRefresh = false) => {
        try {
            if (isRefresh) setRefreshing(true);
            else setLoading(true);
            
            // Obtener órdenes asignadas al técnico actual
            const res = await ordenesServicioAPI.getAll({
                tecnico_id: user?.id,
                estado: 'pendiente,en_proceso' // Solo órdenes activas
            });
            
            // Ordenar por prioridad y fecha
            const sorted = res.data.sort((a, b) => {
                const prioridadOrder = { alta: 0, media: 1, baja: 2 };
                return prioridadOrder[a.prioridad] - prioridadOrder[b.prioridad];
            });
            
            setOrdenes(sorted);
        } catch (err) {
            toast.error('Error al cargar órdenes');
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    const handleTomar = async (ordenId) => {
        setActionLoading(ordenId);
        try {
            await ordenesServicioAPI.tomar(ordenId);
            toast.success('Orden tomada');
            loadOrdenes(true);
        } catch (err) {
            toast.error('Error al tomar la orden');
        } finally {
            setActionLoading(null);
        }
    };

    const handleIniciar = async (ordenId) => {
        setActionLoading(ordenId);
        try {
            await ordenesServicioAPI.iniciar(ordenId);
            toast.success('Orden iniciada');
            loadOrdenes(true);
        } catch (err) {
            toast.error('Error al iniciar la orden');
        } finally {
            setActionLoading(null);
        }
    };

    const openMaps = (direccion) => {
        const encoded = encodeURIComponent(direccion);
        // Intentar abrir en Google Maps o Waze
        window.open(`https://www.google.com/maps/search/?api=1&query=${encoded}`, '_blank');
    };

    const callPhone = (telefono) => {
        window.location.href = `tel:${telefono}`;
    };

    if (loading) {
        return (
            <div className="p-4 space-y-4">
                <Skeleton className="h-8 w-48" />
                {[1, 2, 3].map(i => (
                    <Skeleton key={i} className="h-40 w-full" />
                ))}
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 pb-20">
            {/* Header fijo */}
            <div className="sticky top-0 z-10 bg-white border-b shadow-sm">
                <div className="flex items-center justify-between p-4">
                    <div>
                        <h1 className="text-lg font-bold">Mis Órdenes</h1>
                        <p className="text-sm text-muted-foreground">
                            {ordenes.length} orden{ordenes.length !== 1 ? 'es' : ''} activa{ordenes.length !== 1 ? 's' : ''}
                        </p>
                    </div>
                    <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={() => loadOrdenes(true)}
                        disabled={refreshing}
                    >
                        <RefreshCw className={`h-5 w-5 ${refreshing ? 'animate-spin' : ''}`} />
                    </Button>
                </div>
            </div>

            {/* Lista de órdenes */}
            <div className="p-4 space-y-4">
                {ordenes.length === 0 ? (
                    <Card>
                        <CardContent className="flex flex-col items-center justify-center py-12">
                            <ClipboardList className="h-12 w-12 text-muted-foreground mb-4" />
                            <p className="text-lg font-medium">Sin órdenes pendientes</p>
                            <p className="text-sm text-muted-foreground">
                                No tienes órdenes asignadas en este momento
                            </p>
                        </CardContent>
                    </Card>
                ) : (
                    ordenes.map((orden) => {
                        const estadoConfig = ESTADO_CONFIG[orden.estado] || ESTADO_CONFIG.pendiente;
                        const prioridadConfig = PRIORIDAD_CONFIG[orden.prioridad] || PRIORIDAD_CONFIG.media;
                        const EstadoIcon = estadoConfig.icon;

                        return (
                            <Card 
                                key={orden.id} 
                                className="overflow-hidden"
                                data-testid={`orden-card-${orden.id}`}
                            >
                                {/* Barra de prioridad */}
                                <div className={`h-1 ${prioridadConfig.color}`} />
                                
                                <CardContent className="p-4">
                                    {/* Header de la orden */}
                                    <div className="flex items-start justify-between mb-3">
                                        <div>
                                            <div className="flex items-center gap-2">
                                                <span className="font-bold text-lg">{orden.numero_orden}</span>
                                                <Badge className={estadoConfig.color}>
                                                    <EstadoIcon className="h-3 w-3 mr-1" />
                                                    {estadoConfig.label}
                                                </Badge>
                                            </div>
                                            <p className="text-sm text-muted-foreground">{orden.tipo_servicio_nombre}</p>
                                        </div>
                                        <Badge variant="outline" className="text-xs">
                                            {prioridadConfig.label}
                                        </Badge>
                                    </div>

                                    {/* Información del cliente */}
                                    <div className="space-y-2 mb-4">
                                        <div className="flex items-center gap-2 text-sm">
                                            <User className="h-4 w-4 text-muted-foreground" />
                                            <span className="font-medium">{orden.cliente_nombre || 'Sin cliente'}</span>
                                        </div>
                                        
                                        {orden.vehiculo_patente && (
                                            <div className="flex items-center gap-2 text-sm">
                                                <Car className="h-4 w-4 text-muted-foreground" />
                                                <span>{orden.vehiculo_patente} - {orden.vehiculo_marca} {orden.vehiculo_modelo}</span>
                                            </div>
                                        )}
                                        
                                        {orden.direccion_servicio && (
                                            <button 
                                                className="flex items-center gap-2 text-sm text-blue-600 hover:underline"
                                                onClick={() => openMaps(orden.direccion_servicio)}
                                            >
                                                <MapPin className="h-4 w-4" />
                                                <span className="truncate">{orden.direccion_servicio}</span>
                                            </button>
                                        )}
                                        
                                        {orden.fecha_programada && (
                                            <div className="flex items-center gap-2 text-sm">
                                                <Calendar className="h-4 w-4 text-muted-foreground" />
                                                <span>{new Date(orden.fecha_programada).toLocaleDateString('es-CL')}</span>
                                            </div>
                                        )}
                                    </div>

                                    {/* Acciones rápidas */}
                                    <div className="flex gap-2">
                                        {orden.cliente_telefono && (
                                            <Button 
                                                variant="outline" 
                                                size="sm"
                                                onClick={() => callPhone(orden.cliente_telefono)}
                                                className="flex-1"
                                            >
                                                <Phone className="h-4 w-4 mr-1" />
                                                Llamar
                                            </Button>
                                        )}
                                        
                                        {orden.direccion_servicio && (
                                            <Button 
                                                variant="outline" 
                                                size="sm"
                                                onClick={() => openMaps(orden.direccion_servicio)}
                                                className="flex-1"
                                            >
                                                <Navigation className="h-4 w-4 mr-1" />
                                                Navegar
                                            </Button>
                                        )}
                                        
                                        {orden.estado === 'pendiente' && !orden.tecnico_id && (
                                            <Button 
                                                size="sm"
                                                onClick={() => handleTomar(orden.id)}
                                                disabled={actionLoading === orden.id}
                                                className="flex-1"
                                            >
                                                {actionLoading === orden.id ? (
                                                    <Loader2 className="h-4 w-4 animate-spin" />
                                                ) : (
                                                    <>
                                                        <Play className="h-4 w-4 mr-1" />
                                                        Tomar
                                                    </>
                                                )}
                                            </Button>
                                        )}
                                        
                                        {orden.estado === 'pendiente' && orden.tecnico_id && (
                                            <Button 
                                                size="sm"
                                                onClick={() => handleIniciar(orden.id)}
                                                disabled={actionLoading === orden.id}
                                                className="flex-1"
                                            >
                                                {actionLoading === orden.id ? (
                                                    <Loader2 className="h-4 w-4 animate-spin" />
                                                ) : (
                                                    <>
                                                        <Play className="h-4 w-4 mr-1" />
                                                        Iniciar
                                                    </>
                                                )}
                                            </Button>
                                        )}
                                    </div>

                                    {/* Ver detalle */}
                                    <Button 
                                        variant="ghost" 
                                        className="w-full mt-3"
                                        onClick={() => navigate(`/ordenes/orden/${orden.id}`)}
                                    >
                                        Ver Detalle
                                        <ChevronRight className="h-4 w-4 ml-1" />
                                    </Button>
                                </CardContent>
                            </Card>
                        );
                    })
                )}
            </div>

            {/* Bottom Navigation */}
            <div className="fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg">
                <div className="flex justify-around py-2">
                    <button 
                        className="flex flex-col items-center p-2 text-blue-600"
                        onClick={() => navigate('/ordenes/tecnico')}
                    >
                        <ClipboardList className="h-6 w-6" />
                        <span className="text-xs mt-1">Órdenes</span>
                    </button>
                    <button 
                        className="flex flex-col items-center p-2 text-muted-foreground"
                        onClick={() => navigate('/ordenes/dashboard')}
                    >
                        <Calendar className="h-6 w-6" />
                        <span className="text-xs mt-1">Dashboard</span>
                    </button>
                    <button 
                        className="flex flex-col items-center p-2 text-muted-foreground"
                        onClick={() => navigate('/configuracion/notificaciones')}
                    >
                        <User className="h-6 w-6" />
                        <span className="text-xs mt-1">Perfil</span>
                    </button>
                </div>
            </div>
        </div>
    );
}
